import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import Layout from "../components/Layout";
import * as XLSX from "xlsx";

function CdmOrdersReport() {
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedRows, setExpandedRows] = useState({}); // orderId -> boolean
  const [clientId, setClientId] = useState("");

  useEffect(() => {
    const fetchClientId = async () => {
      try {
        const snap = await getDoc(doc(db, "companyInfo", "default"));
        if (snap.exists()) {
          setClientId(snap.data().clientId || "");
        }
      } catch (err) {
        console.error("Error fetching client ID:", err);
      }
    };
    fetchClientId();
  }, []);

  const loadReport = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      // Fetch all cdmOrders for the client
      const q = query(
        collection(db, "cdmOrders"),
        where("clientId", "==", clientId)
      );
      const snap = await getDocs(q);
      let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Filter by Date range locally (based on createdAt timestamp)
      const from = fromDate ? new Date(fromDate + "T00:00:00") : null;
      const to = toDate ? new Date(toDate + "T23:59:59") : null;

      list = list.filter(o => {
        if (!o.createdAt?.toDate) return false;
        const d = o.createdAt.toDate();
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });

      // Sort: Newest first
      list.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());

      setRows(list);
    } catch (err) {
      console.error("Error loading CDM report:", err);
      alert("Error loading report data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      loadReport();
    }
  }, [fromDate, toDate, clientId]);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const exportXLSX = () => {
    if (filteredRows.length === 0) {
      alert("No data to export!");
      return;
    }

    const excelData = [];
    filteredRows.forEach((r) => {
      const dateStr = r.createdAt?.toDate 
        ? r.createdAt.toDate().toLocaleString("en-IN")
        : "-";

      // Flatten items into Excel rows to show granular product details
      Object.entries(r.items || {}).forEach(([key, details]) => {
        excelData.push({
          "Bill ID": r.id,
          "Date & Time": dateStr,
          "Hawker ID": r.partyId || "",
          "Hawker Name": r.partyName || "",
          "Created By": r.createdBy || "",
          "Category": details.category || "",
          "Division": details.division || "",
          "Product Name": details.productName || "",
          "MSKU": details.msku || key,
          "Rate": details.rate || 0,
          "Quantity": details.qty || 0,
          "Total Item Value": (details.qty || 0) * (details.rate || 0),
          "Grand Total Qty": r.totalQty,
          "Grand Total Amount": r.totalAmount,
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CDM Orders");
    XLSX.writeFile(wb, `CDM_Orders_Report_${fromDate}_to_${toDate}.xlsx`);
  };

  const filteredRows = rows.filter((r) => {
    const term = search.toLowerCase();
    return (
      (r.partyId || "").toLowerCase().includes(term) ||
      (r.partyName || "").toLowerCase().includes(term) ||
      (r.createdBy || "").toLowerCase().includes(term)
    );
  });

  return (
    <Layout>
      <div style={{ padding: "40px 20px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          
          {/* HEADER */}
          <div style={{ borderLeft: "6px solid #673ab7", paddingLeft: 20, marginBottom: 32 }}>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.025em" }}>
              CDM Orders Report
            </h1>
            <p style={{ marginTop: 4, fontSize: 16, color: "#64748b", fontWeight: 500 }}>
              Track, inspect, and export submitted CDM billing sheets for Hawkers
            </p>
          </div>

          {/* FILTERS */}
          <div style={{ background: "#ffffff", borderRadius: 16, padding: "20px 24px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", marginBottom: 24 }}>
            <div style={{ display: "flex", flexDir: "column", gap: 6 }}>
              <label style={labelStyle}>From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div style={{ display: "flex", flexDir: "column", gap: 6 }}>
              <label style={labelStyle}>To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={inputStyle}
              />
            </div>

            <button onClick={loadReport} disabled={loading} style={primaryActionStyle}>
              {loading ? "Loading..." : "🔄 Refresh"}
            </button>

            <button onClick={exportXLSX} style={{ ...primaryActionStyle, background: "#10b981", boxShadow: "0 4px 6px -1px rgba(16, 185, 129, 0.3)" }}>
              📊 Export XLSX
            </button>

            <div style={{ flex: 1, minWidth: 250, display: "flex", flexDir: "column", gap: 6 }}>
              <label style={labelStyle}>Search Hawker</label>
              <input
                type="text"
                placeholder="Search Hawker Name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ ...inputStyle, width: "100%" }}
              />
            </div>
          </div>

          {/* TABLE CONTAINER */}
          <div style={{ background: "#ffffff", borderRadius: 16, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ ...tableHeaderStyle, width: "60px" }}></th>
                    <th style={tableHeaderStyle}>DATE & TIME</th>
                    <th style={tableHeaderStyle}>HAWKER NAME</th>
                    <th style={tableHeaderStyle}>HAWKER ID</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>TOTAL QTY</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>TOTAL VALUE</th>
                    <th style={tableHeaderStyle}>SUBMITTED BY</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => {
                    const formattedTime = r.createdAt?.toDate
                      ? r.createdAt.toDate().toLocaleString("en-IN")
                      : "-";
                    const isExpanded = !!expandedRows[r.id];

                    return (
                      <React.Fragment key={r.id}>
                        {/* Parent Row */}
                        <tr 
                          onClick={() => toggleRow(r.id)}
                          style={{ 
                            borderBottom: "1px solid #f1f5f9", 
                            cursor: "pointer", 
                            background: isExpanded ? "#fbfaff" : "#ffffff",
                            transition: "background 0.2s" 
                          }}
                        >
                          <td style={{ padding: "16px 24px", textAlign: "center", fontWeight: "900", color: "#673ab7", fontSize: "16px" }}>
                            {isExpanded ? "▼" : "▶"}
                          </td>
                          <td style={{ padding: "16px 24px", color: "#475569", fontWeight: "600" }}>{formattedTime}</td>
                          <td style={{ padding: "16px 24px", fontWeight: "800", color: "#1e293b" }}>{r.partyName}</td>
                          <td style={{ padding: "16px 24px", color: "#64748b" }}>{r.partyId}</td>
                          <td style={{ padding: "16px 24px", textAlign: "right", fontWeight: "700", color: "#1e293b" }}>{r.totalQty}</td>
                          <td style={{ padding: "16px 24px", textAlign: "right", fontWeight: "800", color: "#2e7d32" }}>
                            ₹ {Number(r.totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: "16px 24px", color: "#64748b" }}>{r.createdBy}</td>
                        </tr>

                        {/* Expandable Details Row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="7" style={{ padding: "20px 40px", background: "#fbfaff", borderBottom: "1px solid #edf2f7" }}>
                              <div style={{ background: "#ffffff", borderRadius: 12, padding: "16px 20px", border: "1px solid #e2e8f0", boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)" }}>
                                <h4 style={{ margin: "0 0 12px 0", color: "#673ab7", fontWeight: "800", fontSize: "14px" }}>Itemized Breakdown</h4>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                  <thead>
                                    <tr style={{ borderBottom: "2px solid #e2e8f0", background: "#f8fafc" }}>
                                      <th style={nestedHeaderStyle}>PRODUCT NAME</th>
                                      <th style={nestedHeaderStyle}>MSKU</th>
                                      <th style={nestedHeaderStyle}>DIVISION</th>
                                      <th style={nestedHeaderStyle}>CATEGORY</th>
                                      <th style={{ ...nestedHeaderStyle, textAlign: "right" }}>RATE</th>
                                      <th style={{ ...nestedHeaderStyle, textAlign: "right" }}>QTY</th>
                                      <th style={{ ...nestedHeaderStyle, textAlign: "right" }}>TOTAL VALUE</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(r.items || {}).map(([key, details]) => (
                                      <tr key={key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                        <td style={{ padding: "10px 12px", fontWeight: "700", color: "#1e293b" }}>{details.productName}</td>
                                        <td style={{ padding: "10px 12px", color: "#475569" }}>{details.msku || key}</td>
                                        <td style={{ padding: "10px 12px" }}>
                                          <span style={{ fontSize: "10px", background: "#f3e5f5", color: "#673ab7", padding: "2px 6px", borderRadius: 4, fontWeight: "800" }}>{details.division}</span>
                                        </td>
                                        <td style={{ padding: "10px 12px", color: "#64748b" }}>{details.category}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>₹{Number(details.rate || 0).toFixed(2)}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "700" }}>{details.qty}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "700", color: "#2e7d32" }}>
                                          ₹{Number((details.qty || 0) * (details.rate || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan="7" style={{ padding: 100, textAlign: "center" }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>🔎</div>
                        <h3 style={{ color: "#1e293b", margin: 0 }}>No CDM Bills Found</h3>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const primaryActionStyle = {
  padding: "10px 24px",
  fontSize: "14px",
  fontWeight: 700,
  background: "linear-gradient(135deg, #673ab7 0%, #512da8 100%)",
  color: "#ffffff",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(103, 58, 183, 0.3)",
};

const inputStyle = {
  padding: "10px 16px",
  fontSize: "14px",
  borderRadius: "10px",
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#1e293b",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontSize: "11px",
  fontWeight: 800,
  color: "#94a3b8",
  marginBottom: "4px",
  letterSpacing: "0.05em",
  textTransform: "uppercase"
};

const tableHeaderStyle = {
  padding: "16px 24px",
  fontSize: "11px",
  fontWeight: 800,
  color: "#64748b",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  textAlign: "left",
  borderBottom: "1px solid #e2e8f0"
};

const nestedHeaderStyle = {
  padding: "8px 12px",
  fontSize: "10px",
  fontWeight: 800,
  color: "#64748b",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  textAlign: "left"
};

export default CdmOrdersReport;
